import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CategoryFilter from './components/CategoryFilter';
import ProductCard from './components/ProductCard';
import InstallPWA from '../../components/InstallPWA';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { productService, type Product, type Category } from '../../services/productService';
import { ProductCardSkeleton, CategoryFilterSkeleton } from '../../components/feedback/LoadingStates';

export default function Home() {
  const navigate = useNavigate();
  const { cart, addItem, itemsCount } = useCart();
  const { user } = useAuth();
  
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    
    // Add JSON-LD structured data
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Bakery",
      "name": "Saboré",
      "description": "Confeitaria artesanal especializada em bolos e doces feitos com muito carinho",
      "url": `${window.location.origin}/`,
      "telephone": "+55-21-99852-5154",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "BR",
        "addressLocality": "Rio de Janeiro",
        "addressRegion": "RJ"
      },
      "openingHours": "Mo-Su 08:00-20:00",
      "servesCuisine": "Confeitaria",
      "priceRange": "$$",
      "hasMenu": {
        "@type": "Menu",
        "name": "Cardápio Saboré",
        "description": "Bolos, tortas, docinhos e sobremesas artesanais"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "127"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    // Update page title and meta description
    document.title = 'Saboré - Confeitaria Artesanal';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Bolos e doces artesanais feitos com muito carinho. Delivery de confeitaria com os melhores sabores da cidade.');
    }

    return () => {
      // Cleanup script on unmount
      const scripts = document.head.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => script.remove());
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Buscar categorias e produtos em paralelo usando os serviços
      const [categoriesResult, productsResult] = await Promise.all([
        productService.getCategories(),
        productService.getProducts()
      ]);
      
      if (categoriesResult.error) {
        console.error('Erro ao carregar categorias:', categoriesResult.error);
      } else {
        setCategories(categoriesResult.data || []);
      }
      
      if (productsResult.error) {
        console.error('Erro ao carregar produtos:', productsResult.error);
      } else {
        setProducts(productsResult.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (product: Product) => {
    await addItem({
      id: product.id,
      name: product.name,
      description: product.description,
      price: product.price,
      image_url: product.image_url,
    });
  };

  const filteredProducts = selectedCategory === 'all' 
    ? products.filter(p => p.active && p.in_stock)
    : products.filter(p => p.active && p.in_stock && p.category_id === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
        <div className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm border-b border-pink-100 z-50">
          <div className="px-4 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-gray-800" style={{ fontFamily: '"Pacifico", serif' }}>
                  Saboré
                </h1>
                <p className="text-xs text-gray-500">Confeitaria Artesanal</p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => navigate('/profile')}
                  className="w-10 h-10 bg-gradient-to-r from-pink-400 to-purple-400 rounded-full flex items-center justify-center text-white hover:from-pink-500 hover:to-purple-500 transition-all duration-200"
                >
                  <i className="ri-user-line text-sm"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-20 pb-24 px-4">
          <div className="mb-6">
            <div className="bg-gradient-to-r from-pink-400 to-purple-400 rounded-2xl p-6 text-white relative overflow-hidden">
              <div className="relative z-10">
                <h2 className="text-xl font-bold mb-2">Sabores que Encantam</h2>
                <p className="text-pink-100 text-sm">Bolos e doces artesanais feitos com muito carinho</p>
              </div>
              <div className="absolute -right-4 -top-4 w-20 h-20 bg-white/10 rounded-full"></div>
              <div className="absolute -right-8 -bottom-8 w-32 h-32 bg-white/5 rounded-full"></div>
            </div>
          </div>

          <CategoryFilterSkeleton />

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <ProductCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
      {/* Header */}
      <div className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm border-b border-pink-100 z-50">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-800" style={{ fontFamily: '"Pacifico", serif' }}>
                Saboré
              </h1>
              <p className="text-xs text-gray-500">Confeitaria Artesanal</p>
            </div>
            <div className="flex items-center space-x-3">
              {user?.is_admin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="w-10 h-10 bg-gradient-to-r from-orange-400 to-red-400 rounded-full flex items-center justify-center text-white hover:from-orange-500 hover:to-red-500 transition-all duration-200 shadow-md"
                  title="Painel Administrativo"
                >
                  <i className="ri-dashboard-3-line text-base"></i>
                </button>
              )}
              <button
                onClick={() => navigate('/profile')}
                className="w-10 h-10 bg-gradient-to-r from-pink-400 to-purple-400 rounded-full flex items-center justify-center text-white hover:from-pink-500 hover:to-purple-500 transition-all duration-200"
                title="Meu Perfil"
              >
                <i className="ri-user-line text-sm"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="pt-20 pb-24 px-4">
        {/* Hero Section */}
        <div className="mb-6">
          <div className="bg-gradient-to-r from-pink-400 to-purple-400 rounded-2xl p-6 text-white relative overflow-hidden">
            <div className="relative z-10">
              <h2 className="text-xl font-bold mb-2">Sabores que Encantam</h2>
              <p className="text-pink-100 text-sm">Bolos e doces artesanais feitos com muito carinho</p>
            </div>
            <div className="absolute -right-4 -top-4 w-20 h-20 bg-white/10 rounded-full"></div>
            <div className="absolute -right-8 -bottom-8 w-32 h-32 bg-white/5 rounded-full"></div>
          </div>
        </div>

        {/* Categories */}
        <CategoryFilter 
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
        />

        {/* Products Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredProducts.map(product => (
            <ProductCard
              key={product.id}
              product={product}
              onAddToCart={() => handleAddToCart(product)}
              cartQuantity={cart.find(item => item.id === product.id)?.quantity || 0}
            />
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-cake-line text-4xl text-gray-300 mb-4"></i>
            <p className="text-gray-500">Nenhum produto encontrado nesta categoria</p>
          </div>
        )}
      </div>

      {/* Floating Cart Button */}
      {itemsCount > 0 && (
        <button
          onClick={() => navigate('/cart')}
          className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center z-50 hover:scale-105"
        >
          <i className="ri-shopping-cart-line text-white text-xl"></i>
          <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
            <span className="text-white text-xs font-bold">{itemsCount}</span>
          </div>
        </button>
      )}

      {/* PWA Install Prompt */}
      <InstallPWA />
    </div>
  );
}
